import { useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { AlertTriangle, Check } from 'lucide-react';
import { Button } from '@/components/ui';
import { cn } from '@/lib/utils';
import { getUserData } from '@/utils/cookies';

/** CTA laranja em pílula — reutilizado no fluxo (ex.: dimensões de energia). */
export const FORM_WELLBEING_PORTRAIT_CTA_CLASS =
	'min-w-[180px] max-w-full flex-wrap justify-center whitespace-normal text-center leading-snug rounded-full border-none bg-brand-orange px-6 py-3 text-sm font-bold text-white transition-colors duration-200 hover:brightness-[0.96] active:scale-[0.98] disabled:opacity-70 sm:px-8';

export type WellbeingPortraitMockData = {
	scoreValue: number;
	scoreMax?: number;
	/** true: quanto maior, melhor. false: quanto maior, pior. */
	isPositiveImpact?: boolean | null;
	userNameOverride?: string;
};

type FormWellbeingPortraitMockProps = {
	data?: WellbeingPortraitMockData;
	isLoading?: boolean;
	/** Próxima etapa do fluxo (ex.: dimensões de energia). */
	onContinue?: () => void;
};

const fillTemplate = (
	template: string,
	vars: Record<string, string | number>,
) =>
	template.replace(/\{(\w+)\}/g, (_, key: string) => String(vars[key] ?? ''));

const DONUT_R = 88;
const DONUT_STROKE = 14;
const DONUT_C = 2 * Math.PI * DONUT_R;

/** Percentual 0–100 do score; faixas: vermelho &lt; 50, amarelo 50–74, verde ≥ 75. */
const scorePercent01 = (value: number, max: number) => {
	if (max <= 0) return 0;
	return Math.min(100, Math.max(0, (value / max) * 100));
};

/** Inverte a escala de score (ex.: 100->1, 1->100 quando max=100). */
const invertScoreValue = (value: number, max: number) => {
	if (!Number.isFinite(value) || max <= 0) return 0;
	return Math.round(max + 1 - value);
};

const LOW_MAX = 49;
const MODERATE_MAX = 74;

type ScoreBand = 'red' | 'yellow' | 'green';

const getScoreBand = (
	percent: number,
	isPositiveImpact: boolean | null | undefined,
): ScoreBand => {
	if (isPositiveImpact === false) {
		if (percent <= LOW_MAX) return 'green';
		if (percent <= MODERATE_MAX) return 'yellow';
		return 'red';
	}

	if (percent <= LOW_MAX) return 'red';
	if (percent <= MODERATE_MAX) return 'yellow';
	return 'green';
};

const getDefaultStatusLabel = (
	band: ScoreBand,
	_isPositiveImpact: boolean | null | undefined,
): string => {
	if (band === 'red') return 'Bem-estar em Alerta';
	if (band === 'yellow') return 'Bem-estar em Atenção';
	return 'Bem-estar Fortalecido';
};

const bandToClasses = (band: ScoreBand) => {
	switch (band) {
		case 'red':
			return {
				arc: 'text-red-600',
				number: 'text-red-600',
			};
		case 'yellow':
			return {
				arc: 'text-amber-500',
				number: 'text-amber-500',
			};
		default:
			return {
				arc: 'text-emerald-600',
				number: 'text-emerald-600',
			};
	}
};

/** Limites na mesma escala do score (50% e 75% do máximo). */
const tierThresholds = (max: number) => ({
	yellowMin: (50 / 100) * max,
	greenMin: (75 / 100) * max,
});

/** Texto abaixo do status: contexto da escala (50 / 75), sem tom de “jogo” ou meta de pontos. */
const getStatusTierHint = (
	value: number,
	max: number,
	isPositiveImpact: boolean | null | undefined,
): string | null => {
	if (max <= 0) return null;
	const pct = scorePercent01(value, max);
	const band = getScoreBand(pct, isPositiveImpact);
	const { yellowMin, greenMin } = tierThresholds(max);
	const y = Math.round(yellowMin);
	const g = Math.round(greenMin);

	if (isPositiveImpact === false) {
		if (band === 'red') {
			return `Nesse índice, pontuação alta indica maior risco. Você está na faixa alta (a partir de ${g}), então este resultado merece mais atenção e cuidado.`;
		}
		if (band === 'yellow') {
			return `Nesse índice, pontuação alta indica maior risco. Você está na faixa intermediária (${y} a ${
				g - 1
			}), então vale reforçar hábitos de cuidado para evitar piora.`;
		}
		return `Nesse índice, pontuação alta indica maior risco. Seu resultado está na faixa baixa (abaixo de ${y}), sinalizando um cenário mais favorável no momento.`;
	}

	if (band === 'red') {
		return `Esse resultado aponta para um momento que merece mais cuidado com você. Na escala, a próxima faixa é a amarela (atenção moderada), a partir de ${y}. O foco aqui é saúde e bem-estar — ouvir o que você sente e buscar apoio quando fizer sentido.`;
	}
	if (band === 'yellow') {
		return `Você está na faixa amarela. O próximo passo é avançar para um estado mais estável de bem-estar, buscando hábitos e atitudes que realmente te façam se sentir melhor no dia a dia. Na escala, a faixa seguinte é a verde, a partir de ${g}.`;
	}
	return `Você está na faixa verde (a partir de ${g}). É um bom momento para fortalecer o que já te faz bem e manter o cuidado com sua saúde.`;
};

type DonutScoreProps = {
	value: number;
	max: number;
	isPositiveImpact?: boolean | null;
	size?: number;
};

const DonutScore = ({
	value,
	max,
	isPositiveImpact = true,
	size = 240,
}: DonutScoreProps) => {
	const pct = scorePercent01(value, max);
	const dash = (pct / 100) * DONUT_C;
	const band = bandToClasses(getScoreBand(pct, isPositiveImpact));

	return (
		<div
			className="relative flex items-center justify-center"
			style={{ width: size, height: size }}
		>
			<svg
				width={size}
				height={size}
				viewBox="0 0 200 200"
				className="-rotate-90 text-zinc-200"
				aria-hidden
			>
				<circle
					cx="100"
					cy="100"
					r={DONUT_R}
					fill="none"
					stroke="currentColor"
					strokeWidth={DONUT_STROKE}
				/>
				<circle
					cx="100"
					cy="100"
					r={DONUT_R}
					fill="none"
					stroke="currentColor"
					strokeWidth={DONUT_STROKE}
					strokeLinecap="round"
					className={cn(
						band.arc,
						'transition-[stroke-dasharray] duration-700 ease-out',
					)}
					strokeDasharray={`${dash} ${DONUT_C - dash}`}
				/>
			</svg>
			<div className="absolute inset-0 flex flex-col items-center justify-center pt-1">
				<span
					className={cn(
						'text-5xl font-black tabular-nums tracking-tight md:text-6xl',
						band.number,
					)}
				>
					{value}
				</span>
				<span className="text-lg font-semibold tabular-nums text-zinc-400">
					/{max}
				</span>
			</div>
		</div>
	);
};

const FormWellbeingPortraitMock = ({
	data,
	isLoading = false,
	onContinue,
}: FormWellbeingPortraitMockProps) => {
	const scoreMax = data?.scoreMax && data.scoreMax > 0 ? data.scoreMax : 100;
	const hasScore =
		typeof data?.scoreValue === 'number' &&
		Number.isFinite(data.scoreValue);
	const roundedScoreValue = hasScore ? Math.round(data.scoreValue) : 0;
	const invertedScoreValue = hasScore
		? invertScoreValue(roundedScoreValue, scoreMax)
		: 0;

	const displayName = useMemo(() => {
		const fromProp = data?.userNameOverride?.trim();
		if (fromProp) return fromProp;
		const user = getUserData();
		const first = user?.first_name?.trim();
		return first && first.length > 0 ? first : 'Você';
	}, [data?.userNameOverride]);

	const headline = useMemo(
		() =>
			fillTemplate('{name}, mapeamos seu momento atual.', {
				name: displayName,
				score: roundedScoreValue,
				max: scoreMax,
			}),
		[roundedScoreValue, scoreMax, displayName],
	);

	const subline = useMemo(() => {
		if (isLoading) return 'Calculando seu retrato de bem-estar...';
		if (!hasScore)
			return 'Ainda não foi possível gerar seu índice por falta de respostas suficientes.';
		return fillTemplate(
			'Você está com {score}/{max}. Algumas áreas precisam de atenção especial. Vamos ver os detalhes?',
			{
				name: displayName,
				score: invertedScoreValue,
				max: scoreMax,
			},
		);
	}, [displayName, hasScore, isLoading, invertedScoreValue, scoreMax]);

	const handleContinue = () => {
		onContinue?.();
	};

	const scorePct = scorePercent01(invertedScoreValue, scoreMax);
	const statusBand = getScoreBand(scorePct, data?.isPositiveImpact);
	const statusLabel = getDefaultStatusLabel(
		statusBand,
		data?.isPositiveImpact,
	);
	const tierHint = useMemo(
		() =>
			getStatusTierHint(
				invertedScoreValue,
				scoreMax,
				data?.isPositiveImpact,
			),
		[invertedScoreValue, scoreMax, data?.isPositiveImpact],
	);

	const ctaClassName = FORM_WELLBEING_PORTRAIT_CTA_CLASS;

	return (
		<section className="relative flex min-h-full w-full flex-col items-center bg-white px-5 py-8 sm:px-8 md:py-12 lg:px-12 xl:px-16 2xl:px-20">
			<Helmet>
				<title>Seu Retrato de Bem-Estar | Plumaa</title>
				<meta
					name="description"
					content="Veja o seu retrato de bem-estar gerado a partir das respostas do formulário."
				/>
			</Helmet>

			<div className="mx-auto flex w-full max-w-lg flex-col items-center text-center sm:max-w-2xl lg:max-w-4xl xl:max-w-5xl 2xl:max-w-6xl">
				{/* Cabeçalho */}
				<h1 className="w-full text-2xl font-semibold leading-snug tracking-tight text-zinc-800 md:text-[1.75rem]">
					{headline}
				</h1>
				<p className="mt-3 w-full text-base font-normal leading-relaxed text-zinc-500 md:text-[1.05rem]">
					{subline}
				</p>

				{/* Hero card — glass + donut */}
				<article
					className="mt-8 w-full rounded-[1.75rem] border px-5 py-8 backdrop-blur-xl md:px-8 md:py-10"
					style={{ WebkitBackdropFilter: 'blur(16px)' }}
				>
					<p className="text-[10px] font-black uppercase tracking-[0.22em] text-zinc-400">
						Índice Plumaa de Bem-Estar
					</p>

					<div className="mt-5 flex justify-center">
						{hasScore ? (
							<DonutScore
								value={invertedScoreValue}
								max={scoreMax}
								isPositiveImpact={data?.isPositiveImpact}
							/>
						) : (
							<div className="flex min-h-[240px] w-full max-w-[320px] items-center justify-center rounded-2xl border border-dashed border-zinc-300 bg-zinc-50 px-6 text-center">
								<p className="text-sm font-medium text-zinc-500">
									Sem dados suficientes para calcular o seu
									índice nesta etapa.
								</p>
							</div>
						)}
					</div>

					{hasScore && (
						<div className="mt-6 flex flex-col items-center gap-3">
							<span
								className={cn(
									'inline-flex items-center gap-2 rounded-full border px-4 py-2 text-xs font-bold shadow-sm backdrop-blur-sm',
									statusBand === 'red' &&
										'border-red-200/90 bg-red-50/95 text-red-900',
									statusBand === 'yellow' &&
										'border-amber-200/80 bg-amber-50/90 text-amber-900',
									statusBand === 'green' &&
										'border-emerald-200/90 bg-emerald-50/95 text-emerald-900',
								)}
							>
								{statusBand === 'green' ? (
									<Check
										className="size-4 shrink-0 text-emerald-600"
										strokeWidth={2.5}
										aria-hidden
									/>
								) : (
									<AlertTriangle
										className={cn(
											'size-4 shrink-0',
											statusBand === 'red' &&
												'text-red-600',
											statusBand === 'yellow' &&
												'text-amber-600',
										)}
										strokeWidth={2.25}
										aria-hidden
									/>
								)}
								{statusLabel}
							</span>
							{tierHint && (
								<p className="w-full text-center text-[11px] leading-relaxed text-zinc-500 md:text-xs">
									{tierHint}
								</p>
							)}
						</div>
					)}
				</article>

				{/* IPBE — sempre visível */}
				<div className="mt-6 w-full rounded-2xl border border-zinc-200/80 bg-white/60 px-4 py-3 text-left shadow-sm backdrop-blur-sm md:px-5 md:py-4">
					<p className="text-[11px] font-bold uppercase tracking-wide text-zinc-500">
						Índice Plumaa de Bem-Estar{' '}
						<span className="text-brand-orange">(IPBE)</span>
					</p>
					<p className="mt-2 text-xs leading-relaxed text-zinc-600">
						O IPBE (Indice Plumaa de Bem-Estar) combina suas
						respostas sobre energia pessoal, profissional e
						relacional para gerar um retrato do seu momento atual.
					</p>
				</div>

				{/* CTA */}
				<div className="mt-10 flex w-full justify-center pb-2">
					<Button
						type="button"
						text="Ver meu retrato completo"
						onClick={handleContinue}
						className={ctaClassName}
					/>
				</div>
			</div>
		</section>
	);
};

export { FormWellbeingPortraitMock };
